import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { breakpoints, palette, spacing, Button, fontSize } from '@playdapp/ui';

import Image from '@/components/Image';
import SortByDropDown from '@/components/SortByDropDown';

export const ExploreBox = styled.div`
  display: flex;
  width: 100%;
  max-width: ${({ theme }) => theme.rem(1920)};
  margin: 0 auto;
`;

export const EmptyBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 50vh;

  ${breakpoints.down('lg')} {
    min-width: 100%;
  }
`;

export const FilterButton = styled(Button)<{
  outline?: boolean;
  fullSizing?: boolean;
}>`
  border-radius: ${({ theme }) => theme.rem(17)};
  color: ${({ outline }) => !outline && 'white'};
  background: ${({ outline }) => !outline && '#12CEB0'};
  border: ${({ outline }) => !outline && '1px solid #0d917b'};
  font-size: ${({ fullSizing }) => (fullSizing ? fontSize.p3 : fontSize.p5)};
  white-space: nowrap;

  &:hover {
    background: ${({ outline }) => !outline && '#0d917b'};
  }
`;

export const FilterButtonBox = styled.div`
  display: flex;
  align-items: center;

  div {
    margin-left: ${spacing.s};
    font-size: ${fontSize.p4};
    font-weight: 600;
    color: ${palette.primary600};
    cursor: pointer;
  }

  span {
    max-width: ${({ theme }) => theme.rem(180)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-weight: 700;
    font-size: ${fontSize.b5};
    color: ${palette.dgray500};
  }
`;

export const FilterButtonMobile = styled(FilterButton)`
  align-items: center;
  width: ${({ theme }) => theme.rem(80)};
  height: ${({ theme }) => theme.rem(32)};
  font-size: ${fontSize.p4};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.rem(24)};
  border: 0;
`;

export const Content = styled.section`
  position: relative;
  left: ${({ theme }) => theme.rem(308)};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: ${({ theme }) => `calc(100% - ${theme.rem(340)})`};
  overflow: hidden;
  margin-top: 4.5rem;

  ${breakpoints.down('xl')} {
    margin-top: 4rem;
  }

  ${breakpoints.down('lg')} {
    left: ${({ theme }) => theme.rem(265)};
    max-width: ${({ theme }) => `calc(100% - ${theme.rem(265)})`};
  }

  ${breakpoints.down('md')} {
    left: 0;
    max-width: 100%;
    margin-top: 3.5rem;
  }
`;

export const CategoryTitleBox = styled.div<{ isTitleBanner: boolean }>`
  position: relative;
  display: ${({ isTitleBanner }) => (isTitleBanner ? 'flex' : 'none')};
  width: 100%;
  height: 100%;
  margin-top: ${spacing.xxl};

  ${breakpoints.down('lg')} {
    margin-top: ${spacing.s};
  }
`;

export const CategoryTitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.rem(90)};
  width: 100%;
  z-index: 2;

  ${breakpoints.down('lg')} {
    margin-top: ${spacing.xxxl};
    padding-bottom: 0px;
  }

  ${breakpoints.down('md')} {
    margin-top: ${({ theme }) => theme.rem(14)};
    align-items: center;
    border-bottom: 0px;
  }
`;

export const TitleBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: center;
  margin-top: ${({ theme }) => theme.rem(110)};

  ${breakpoints.down('lg')} {
    margin-top: ${({ theme }) => theme.rem(46)};
  }

  ${breakpoints.down('md')} {
    max-width: ${({ theme }) => theme.rem(335)};
    margin-top: 0;
    margin-bottom: 0;
    align-items: center;
  }
`;

export const CategoryName = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => `${theme.rem(30)} ${theme.rem(10)} 0 0`};
  span {
    font-size: ${fontSize.h4};
    font-weight: 700;

    ${breakpoints.down('lg')} {
      font-size: ${fontSize.p1};
      margin: ${({ theme }) => `${theme.rem(12)} ${theme.rem(10)} 0 0`};
      max-width: ${({ theme }) => theme.rem(270)};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: center;
    }

    ${breakpoints.down('md')} {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
      text-align: initial;
      margin: ${spacing.s} 0 0 0;
      max-width: ${({ theme }) => theme.rem(200)};
      font-size: ${fontSize.h4};
      font-size: 1rem;
    }
  }
  ${breakpoints.down('md')} {
    margin-bottom: ${spacing.xs};
    margin-top: 4rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const VisitSiteButton = styled(Button)`
  margin: 0 0 0 ${spacing.m};
  min-width: ${({ theme }) => theme.rem(80)};
  background: inherit;
  color: ${palette.dgray600};
  font-weight: 600;
  font-size: ${fontSize.p4};
  padding: ${({ theme }) =>
    `${theme.rem(6)} ${theme.rem(10)} ${theme.rem(6)} ${theme.rem(14)}`};
  border: 1px solid ${palette.gray900};
  border-radius: 2rem;

  & > a {
    gap: ${({ theme }) => theme.rem(6)};
  }

  ${breakpoints.down('lg')} {
    margin: ${({ theme }) => `${theme.rem(15)} ${theme.rem(10)} 0 0`};
  }

  :hover,
  active {
    background: rgba(153, 156, 168, 0.15);
  }
`;

export const VisitSiteButtonMobile = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.xl};
  height: ${spacing.xl};
  margin: ${spacing.s} 0 0 0;
  margin-left: ${spacing.s};
  border: 1px solid ${palette.gray900};
  border-radius: 100%;
  cursor: pointer;
`;

export const ThumbnailBox = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  display: flex;
  align-self: center;
  margin-top: ${spacing.m};
  margin-right: ${spacing.xl};
  border: 2px solid ${palette.white};
  border-radius: ${({ theme }) => theme.rem(120)};
  z-index: 2;

  ${breakpoints.down('lg')} {
    margin-bottom: 0;
  }

  ${({ isTablet, theme }) =>
    isTablet &&
    css`
      width: ${theme.rem(94)};
      height: ${theme.rem(90)};
      margin-bottom: ${spacing.xxxl};
    `};

  ${({ isMobile, theme }) =>
    isMobile &&
    css`
      width: ${theme.rem(54)};
      height: ${theme.rem(50)};
      margin-top: ${spacing.xl};
      margin-right: ${spacing.l};
      margin-bottom: 0;
    `};
`;

export const CategoryImage = styled(Image)`
  border-radius: 100%;
  z-index: 2;
`;

export const CategoryDesc = styled.div<{ isReadMoreActive?: boolean }>`
  display: ${({ isReadMoreActive }) =>
    isReadMoreActive ? 'flex' : ' -webkit-box'};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  max-width: ${({ theme }) => `calc(100% - ${theme.rem(100)})`};
  max-height: ${({ isReadMoreActive, theme }) =>
    isReadMoreActive ? '100%' : theme.rem(72)};
  margin-top: ${spacing.xs};
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${palette.gray900};

  ${breakpoints.down('md')} {
    position: initial;
    width: 100%;
    max-width: 100%;
    margin: 0;
    margin-bottom: ${spacing.xs};
    font-size: ${fontSize.p5};
    padding-top: 0;
  }
`;

export const CategoryBorder = styled.div`
  display: flex;
  width: 100%;
  height: 1px;
  border-bottom: 1px solid ${palette.gray500};
  margin-top: ${spacing.m};

  ${breakpoints.down('md')} {
    display: none;
  }
`;

export const ReadMore = styled.div<{ isReadMoreActive?: boolean }>`
  position: absolute;
  right: 0;
  bottom: ${({ theme }) => theme.rem(30)};
  text-decoration: underLine;
  color: ${palette.primary600};
  font-size: ${fontSize.p4};
  font-weight: 600;
  cursor: pointer;

  ${breakpoints.down('md')} {
    right: calc(50% - 2rem);
    bottom: 0;
    font-size: ${fontSize.p5};
  }
`;

export const Banner = styled.div<{ url?: string }>`
  position: absolute;
  width: 100%;
  max-width: ${({ theme }) => theme.rem(1580)};
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: ${spacing.xl};

  ${breakpoints.up('lg')} {
    height: ${({ theme }) => theme.rem(210)};
    background-image: image-set(
      url(${({ url }) => url}) 1x,
      url(${({ url }) => url}) 2x
    );
  }

  ${breakpoints.between('md', 'lg')} {
    height: ${({ theme }) => theme.rem(110)};
    background-image: image-set(
      url(${({ url }) => url}) 1x,
      url(${({ url }) => url}) 2x
    );
  }

  ${breakpoints.down('md')} {
    height: ${({ theme }) => theme.rem(80)};
    background-image: image-set(
      url(${({ url }) => url}) 1x,
      url(${({ url }) => url}) 2x
    );
  }
`;

export const ContentWrapper = styled.div<{ isTitleBanner?: boolean }>`
  position: relative;
  width: calc(100% - ${spacing.xxxl});
  max-width: ${({ theme }) => theme.rem(1226)};
  height: 100%;
  padding-bottom: ${spacing.m};
  margin-left: ${spacing.xxxl};
  margin-top: ${({ isTitleBanner }) => !isTitleBanner && spacing.xl};

  ${breakpoints.down('lg')} {
    width: calc(100% - ${spacing.xxxl});
    margin-left: 0;
    margin: 0 auto;
    margin-top: ${({ isTitleBanner }) => !isTitleBanner && spacing.l};
  }

  ${breakpoints.down('md')} {
    width: ${({ theme }) => `calc(100% - ${theme.rem(48)})`};
    margin-top: 0;
  }
`;

export const ContentNavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${spacing.m};

  b {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }
`;

export const ContentNavBarFixed = styled.div<{ isFixed: boolean }>`
  display: flex;
  position: ${({ isFixed }) => isFixed && 'fixed'};
  top: ${({ isFixed, theme }) => isFixed && theme.rem(56)};
  left: ${({ isFixed }) => isFixed && '0px'};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ isFixed, theme }) => isFixed && theme.rem(52)};
  margin-top: ${({ isFixed }) => !isFixed && spacing.l};
  padding: ${({ isFixed, theme }) =>
    isFixed && `${theme.rem(10)} ${spacing.xl}`};
  backdrop-filter: ${({ isFixed }) => isFixed && 'blur(8px)'};
  border-top: ${({ isFixed }) => isFixed && `1px solid ${palette.gray400}`};
  background: ${({ isFixed }) => isFixed && 'rgba(250, 250, 251, 0.95)'};
  box-shadow: ${({ isFixed, theme }) =>
    isFixed && `0px ${theme.rem(4)} ${theme.rem(4)} rgba(0, 0, 0, 0.25)`};
  z-index: ${({ isFixed }) => isFixed && '9'};
`;

export const ContentFooterFixed = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const FixedFooterButton = styled(Button)`
  width: ${({ theme }) => theme.rem(260)};
  height: 2.5rem;
  margin-bottom: 1.25rem;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(8px);
  font-weight: 600;
  font-size: 1rem;
`;

export const SortByDropDownMobile = styled(SortByDropDown)`
  align-items: flex-end;
  border: 0px;
  width: ${({ theme }) => theme.rem(122)};

  & > div {
    padding: 0px;
  }
`;

export const CardListBox = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${spacing.l};

  ${breakpoints.down('xxl')} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${spacing.m};
  }

  ${breakpoints.down('xl')} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: ${spacing.l};
    column-gap: ${spacing.xl};
  }

  ${breakpoints.down('lg')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing.l};
    margin-top: ${spacing.l};
  }

  ${breakpoints.down('md')} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${spacing.m};
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (max-width: 359px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing.s};
  }
`;

export const DropDownSortContent = styled.div`
  display: flex;
  flex-direction: column;
  & > div {
    padding: ${spacing.s} ${spacing.l};
    cursor: pointer;
    ${breakpoints.down('md')} {
      font-size: ${fontSize.p5};
    }
  }
`;
